import { Controller } from "@hotwired/stimulus"

// Modale "Gérer les contacts" d'une liste marketing (refonte Hugo 18/06/2026).
// Pas d'aperçu initial : résultats seulement à la recherche. Les contacts déjà
// dans la liste apparaissent COCHÉS + badge ; décocher = les retirer. L'état de
// sélection persiste entre recherches. Le submit applique le delta :
//   add_ids[]    = cochés qui n'étaient PAS dans la liste
//   remove_ids[] = décochés qui ÉTAIENT dans la liste
export default class extends Controller {
  static targets = ["input", "results", "ids", "submit", "summary"]
  static values  = { url: String, requireEmail: Boolean }

  connect() {
    // selection : id => { name, inList (baseline), checked (courant) }
    this.selection = new Map()
    this.render()
    // Pas de search() initial → pas d'aperçu.
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
  }

  search() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.#fetchResults(), 250)
  }

  async #fetchResults() {
    const q = this.inputTarget.value.trim()
    if (q.length === 0) {
      this.resultsTarget.innerHTML = this.#emptyHtml("Tape un nom, un email ou une société pour chercher.")
      return
    }
    const sep = this.urlValue.includes("?") ? "&" : "?"
    let data = []
    try {
      const resp = await fetch(`${this.urlValue}${sep}q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
      data = await resp.json()
    } catch (_) {
      this.resultsTarget.innerHTML = this.#emptyHtml("Erreur de recherche, réessaie.")
      return
    }
    if (!data.length) {
      this.resultsTarget.innerHTML = this.#emptyHtml(`Aucun contact trouvé pour « ${q} ».`)
      return
    }
    this.resultsTarget.innerHTML = `<ul class="divide-y divide-border-subtle">${data.map(c => this.#rowHtml(c)).join("")}</ul>`
  }

  #rowHtml(c) {
    const id = String(c.id)
    // état courant : ce que l'utilisateur a déjà touché, sinon le baseline (already_in)
    const checked = this.selection.has(id) ? this.selection.get(id).checked : c.already_in
    const blockedNoEmail = this.requireEmailValue && !c.has_email
    const badge = c.already_in
      ? `<span class="picker-badge">déjà dans la liste</span>`
      : ""
    const noEmailNote = blockedNoEmail
      ? `<span class="picker-badge picker-badge--warn">email manquant</span>`
      : ""
    const subtitle = c.subtitle ? `<div class="text-xs text-text-muted truncate">${this.#esc(c.subtitle)}</div>` : ""
    return `
      <li>
        <label class="flex items-center gap-3 py-2.5 px-1 ${blockedNoEmail ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-surface-2"} rounded">
          <input type="checkbox" class="rounded" value="${id}"
                 data-name="${this.#esc(c.name)}"
                 data-already-in="${c.already_in ? "true" : "false"}"
                 data-action="change->list-contact-picker#toggle"
                 ${checked ? "checked" : ""} ${blockedNoEmail ? "disabled" : ""}>
          <div class="min-w-0">
            <div class="text-sm font-medium text-text-primary">${this.#esc(c.name)}${badge}${noEmailNote}</div>
            ${subtitle}
          </div>
        </label>
      </li>`
  }

  toggle(event) {
    const cb = event.target
    const id = String(cb.value)
    this.selection.set(id, {
      name: cb.dataset.name,
      inList: cb.dataset.alreadyIn === "true",
      checked: cb.checked
    })
    this.render()
  }

  render() {
    let toAdd = 0
    let toRemove = 0
    const addInputs = []
    const removeInputs = []
    for (const [ id, v ] of this.selection.entries()) {
      if (v.checked && !v.inList) {
        toAdd++
        addInputs.push(`<input type="hidden" name="add_ids[]" value="${id}">`)
      } else if (!v.checked && v.inList) {
        toRemove++
        removeInputs.push(`<input type="hidden" name="remove_ids[]" value="${id}">`)
      }
    }

    this.idsTarget.innerHTML = addInputs.join("") + removeInputs.join("")

    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = (toAdd || toRemove)
        ? `${toAdd} à ajouter · ${toRemove} à retirer`
        : ""
    }

    if (this.hasSubmitTarget) {
      const total = toAdd + toRemove
      this.submitTarget.disabled = total === 0
      this.submitTarget.textContent = total === 0
        ? "Appliquer"
        : `Appliquer (${toAdd} ajout${toAdd > 1 ? "s" : ""}, ${toRemove} retrait${toRemove > 1 ? "s" : ""})`
    }
  }

  #emptyHtml(msg) {
    return `<p class="text-sm text-text-muted py-6 text-center">${this.#esc(msg)}</p>`
  }

  #esc(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML
  }
}
